import React, { useState, useEffect, useMemo } from 'react';
import { Smartphone, Download, Search, QrCode, Copy, CheckCircle, Loader2 } from 'lucide-react';
import api from '../services/api';

interface EmployeeRow {
  id: number;
  employeeCode?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  departmentName?: string;
}

interface InviteResult {
  inviteId: number;
  inviteToken: string;
  qrContent: string;
  expiresAt: string;
  employee?: { id: number; fullName: string };
}

const qrPngDataUrl = (content: string, size = 260) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=0&data=${encodeURIComponent(content)}`;

const MobileInvite: React.FC = () => {
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<EmployeeRow | null>(null);
  const [invite, setInvite] = useState<InviteResult | null>(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const r: any = await api.get('/employees?pageSize=1000&isActive=true');
        const raw = r?.data ?? r;
        const arr: any[] = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
        const rows: EmployeeRow[] = arr.map((e: any) => ({
          id: e.id ?? e.employeeId,
          employeeCode: e.employeeCode ?? e.idNumber,
          firstName: e.firstName, lastName: e.lastName,
          fullName: e.fullName ?? `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim(),
          departmentName: e.departmentName ?? 'Genel',
        }));
        setEmployees(rows);
      } catch (e: any) {
        setError(e?.response?.data?.message ?? 'Personel listesi yuklenemedi');
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr-TR');
    if (!q) return employees;
    return employees.filter(e =>
      (e.fullName ?? '').toLocaleLowerCase('tr-TR').includes(q) ||
      (e.employeeCode ?? '').toLocaleLowerCase('tr-TR').includes(q) ||
      (e.departmentName ?? '').toLocaleLowerCase('tr-TR').includes(q)
    );
  }, [employees, search]);

  const handleSelect = async (emp: EmployeeRow) => {
    setSelected(emp);
    setInvite(null);
    setError(null);
    setGenerating(true);
    try {
      const r: any = await api.post('/mobile/invites', { employeeId: emp.id, validDays: 7 });
      const data: InviteResult = r?.data ?? r;
      setInvite(data);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'QR davet olusturulamadi');
    } finally { setGenerating(false); }
  };

  const copyToken = async () => {
    if (!invite?.inviteToken) return;
    await navigator.clipboard.writeText(invite.inviteToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-6 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-800">Mobil Cihaz Daveti</h1>
          <p className="text-sm text-slate-500 mt-1">
            Personele NexVera PDKS Android uygulaması için QR davet kodu üretin
          </p>
        </div>
        <a
          href="/downloads/nexvera-pdks.apk"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-bold shadow-lg shadow-orange-500/30 hover:from-amber-600 hover:to-orange-600 transition-all"
        >
          <Download size={18} />
          APK İndir
        </a>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* SOL: Personel Sec */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Smartphone size={20} className="text-blue-600" />
            <h2 className="text-lg font-black text-slate-800">Personel Seç</h2>
          </div>

          <div className="relative mb-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Ad, soyad veya personel no..."
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
            />
          </div>

          <div className="max-h-[520px] overflow-y-auto space-y-1 pr-1">
            {loading ? (
              <div className="p-8 text-center text-slate-400 text-sm">
                <Loader2 size={20} className="animate-spin inline mr-2" /> Yükleniyor…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">Personel bulunamadı</div>
            ) : filtered.map(emp => (
              <button
                key={emp.id}
                onClick={() => handleSelect(emp)}
                className={`w-full text-left px-4 py-3 rounded-xl transition-all border ${
                  selected?.id === emp.id
                    ? 'bg-blue-50 border-blue-300'
                    : 'bg-white border-transparent hover:bg-slate-50'
                }`}
              >
                <p className="font-bold text-slate-800 text-sm">{emp.fullName}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  #{emp.employeeCode ?? '—'} · {emp.departmentName}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* SAG: QR ve link */}
        <div className="bg-slate-900 rounded-2xl p-6 shadow-lg text-slate-100 flex flex-col">
          <div className="flex items-center gap-2 mb-4">
            <QrCode size={20} className="text-amber-400" />
            <h2 className="text-lg font-black text-white">QR Davet</h2>
          </div>

          {!selected && !generating && (
            <div className="flex-1 flex flex-col items-center justify-center py-16 text-center text-slate-500">
              <QrCode size={80} className="opacity-20 mb-4" />
              <p className="text-sm">Sol taraftan personel seçip<br />QR davet oluşturun</p>
            </div>
          )}

          {generating && (
            <div className="flex-1 flex flex-col items-center justify-center py-16 text-slate-400">
              <Loader2 size={32} className="animate-spin mb-3" />
              <p className="text-sm">Davet oluşturuluyor…</p>
            </div>
          )}

          {invite && !generating && (
            <div className="flex-1 flex flex-col items-center text-center">
              <div className="p-4 bg-white rounded-2xl shadow-inner mb-4">
                <img
                  src={qrPngDataUrl(invite.qrContent, 260)}
                  alt="QR"
                  className="w-64 h-64"
                />
              </div>
              <p className="text-lg font-bold text-white">{invite.employee?.fullName ?? selected?.fullName}</p>
              <p className="text-xs text-slate-400 mt-1">
                Geçerlilik: {new Date(invite.expiresAt).toLocaleString('tr-TR')}
              </p>

              <div className="w-full mt-4 p-3 bg-slate-800/50 border border-slate-700 rounded-xl">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Davet Kodu</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs font-mono text-amber-300 break-all select-all">
                    {invite.inviteToken}
                  </code>
                  <button
                    onClick={copyToken}
                    className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                    title="Kopyala"
                  >
                    {copied
                      ? <CheckCircle size={16} className="text-emerald-400" />
                      : <Copy size={16} className="text-slate-400" />}
                  </button>
                </div>
              </div>

              <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">
                Personel APK'yı yükleyip bu QR'ı kameraya okuttuğunda cihazı otomatik kaydedilir.
                Davet tek kullanımlıktır.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileInvite;
